// BOM (자재명세서) — 품번별 자재 구성 + explode 계산기
import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Card } from '../components/Card'
import { Button } from '../components/Button'
import { SkeletonRows } from '../components/Skeleton'
import { api } from '../lib/api'
import { useToast } from '../components/Toast'
import { useGlobalReveal } from '../hooks/useReveal'
import { useT } from '../lib/i18n'

interface BomHead {
  bom_id: string
  product_item_code: string
  version: string
  uom: string
  base_qty: number
  active: number
  notes: string | null
  item_count: number
}

interface BomItem {
  bom_item_id: string
  component_item_code: string
  qty_per: number
  uom: string
  scrap_percent: number
  position: number
}

interface BomDetail { header: BomHead; items: BomItem[] }

interface Exploded {
  product_item_code: string
  produce_qty: number
  uom: string
  bom_version: string
  requirements: Array<{
    component_item_code: string
    qty_per: number
    uom: string
    scrap_percent: number
    gross_qty: number
    scrap_qty: number
    required_qty: number
  }>
}

export function BomsPage() {
  useGlobalReveal()
  const toast = useToast()
  const { t } = useT()
  const [list, setList] = useState<BomHead[] | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [detail, setDetail] = useState<BomDetail | null>(null)
  const [explodeQty, setExplodeQty] = useState<string>('100')
  const [exploded, setExploded] = useState<Exploded | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    api.get<BomHead[]>('/boms')
      .then((d) => { setList(d); if (d[0]) setSelected(d[0].bom_id) })
      .catch((e) => { setList([]); toast.push('error', e?.message ?? '조회 실패') })
  }, [toast])

  useEffect(() => {
    if (!selected) return
    setDetail(null); setExploded(null)
    api.get<BomDetail>(`/boms/${selected}`)
      .then(setDetail)
      .catch((e) => toast.push('error', e?.message ?? '상세 로드 실패'))
  }, [selected, toast])

  const doExplode = async () => {
    if (!detail) return
    const qty = Number(explodeQty)
    if (!Number.isFinite(qty) || qty <= 0) { toast.push('error', '수량은 양의 숫자'); return }
    setBusy(true)
    try {
      const r = await api.get<Exploded>(
        `/boms/explode/calc?item=${encodeURIComponent(detail.header.product_item_code)}&qty=${qty}`,
      )
      setExploded(r)
    } catch (e: any) {
      toast.push('error', e?.message ?? '계산 실패')
    } finally { setBusy(false) }
  }

  return (
    <main id="main" className="container stack" style={{ paddingBlock: '2rem' }}>
      <h1 className="page-title reveal">
        <FontAwesomeIcon icon="list-check" /> {t('bom.title')}
      </h1>
      <p className="muted reveal">{t('bom.intro')}</p>

      <div className="reveal" style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'minmax(280px, 1fr) 2fr' }}>
        <Card glass tilt={false} spotlight={false} as="section" aria-labelledby="boms-h">
          <h2 id="boms-h" style={{ fontSize: 'var(--fs-lg)' }}>활성 BOM</h2>
          {!list ? <SkeletonRows rows={4} /> : list.length === 0 ? (
            <p className="muted">활성 BOM이 없습니다.</p>
          ) : (
            <ul style={{ paddingLeft: 0, listStyle: 'none', display: 'grid', gap: '0.4rem' }}>
              {list.map((b) => (
                <li key={b.bom_id}>
                  <button
                    onClick={() => setSelected(b.bom_id)}
                    aria-current={selected === b.bom_id ? 'true' : undefined}
                    style={{
                      width: '100%', textAlign: 'left',
                      padding: '0.6rem 0.8rem',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid ' + (selected === b.bom_id ? 'var(--color-accent)' : 'var(--color-line)'),
                      background: selected === b.bom_id ? 'var(--color-accent-soft)' : 'var(--color-surface)',
                      cursor: 'pointer',
                      color: 'inherit',
                    }}
                  >
                    <div className="row space-between" style={{ alignItems: 'baseline' }}>
                      <strong className="mono">{b.product_item_code}</strong>
                      <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{b.version}</span>
                    </div>
                    <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>{b.item_count} 자재 · 기준 {b.base_qty} {b.uom}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <div className="stack">
          {!detail ? (
            <Card glass tilt={false} spotlight={false}><SkeletonRows rows={5} /></Card>
          ) : (
            <>
              <Card glass tilt={false} spotlight={false} as="section">
                <div className="row space-between">
                  <div>
                    <h2 style={{ fontSize: 'var(--fs-lg)', marginBottom: '0.3rem' }}>
                      <span className="mono">{detail.header.product_item_code}</span>{' '}
                      <span className="muted">{detail.header.version}</span>
                    </h2>
                    {detail.header.notes && <p className="muted" style={{ marginBottom: 0 }}>{detail.header.notes}</p>}
                  </div>
                  <span className="badge info">기준 {detail.header.base_qty} {detail.header.uom}</span>
                </div>
              </Card>

              <Card glass tilt={false} spotlight={false} as="section" aria-labelledby="items-h">
                <h2 id="items-h" style={{ fontSize: 'var(--fs-lg)' }}>구성 자재 ({detail.items.length})</h2>
                <div className="table-wrap" style={{ '--table-min-w': '580px' } as React.CSSProperties}>
                  <table className="table" aria-label="BOM 자재 목록">
                    <thead>
                      <tr><th>#</th><th>자재 품번</th><th>소요량/단위</th><th>UoM</th><th>스크랩%</th></tr>
                    </thead>
                    <tbody>
                      {detail.items.map((i) => (
                        <tr key={i.bom_item_id}>
                          <td className="mono">{i.position}</td>
                          <td className="mono">{i.component_item_code}</td>
                          <td className="mono">{i.qty_per}</td>
                          <td>{i.uom}</td>
                          <td className="mono">{i.scrap_percent}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>

              <Card glass tilt={false} spotlight={false} as="section" aria-labelledby="exp-h">
                <h2 id="exp-h" style={{ fontSize: 'var(--fs-lg)' }}>
                  <FontAwesomeIcon icon="calculator" /> 소요량 계산 (explode)
                </h2>
                <div className="row" style={{ alignItems: 'end', marginBottom: '1rem' }}>
                  <div className="field" style={{ flex: '1 1 220px', marginBottom: 0 }}>
                    <label htmlFor="exp-qty">생산 수량 ({detail.header.uom})</label>
                    <input
                      id="exp-qty"
                      type="number"
                      min="1"
                      step="1"
                      placeholder="100"
                      value={explodeQty}
                      onChange={(e) => setExplodeQty(e.target.value)}
                    />
                  </div>
                  <Button variant="accent" magnetic={false} disabled={busy} onClick={doExplode}>
                    <FontAwesomeIcon icon={busy ? 'circle-notch' : 'calculator'} spin={busy} />
                    <span>계산</span>
                  </Button>
                </div>

                {exploded && (
                  <>
                    <p className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
                      <span className="mono">{exploded.product_item_code}</span> {exploded.produce_qty} {exploded.uom} 생산 시 필요 자재:
                    </p>
                    <div className="table-wrap" style={{ '--table-min-w': '700px' } as React.CSSProperties}>
                      <table className="table" aria-label="필요 자재 계산 결과">
                        <thead>
                          <tr><th>자재</th><th>기본</th><th>스크랩</th><th>총 필요량</th><th>UoM</th></tr>
                        </thead>
                        <tbody>
                          {exploded.requirements.map((r) => (
                            <tr key={r.component_item_code}>
                              <td className="mono">{r.component_item_code}</td>
                              <td className="mono">{r.gross_qty.toFixed(3)}</td>
                              <td className="mono muted">+{r.scrap_qty.toFixed(3)} ({r.scrap_percent}%)</td>
                              <td className="mono"><strong>{r.required_qty.toFixed(3)}</strong></td>
                              <td>{r.uom}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </Card>
            </>
          )}
        </div>
      </div>
    </main>
  )
}
